<template>
  <div class="page-document">
    <h1 class="title">
      {{ isEditMode ? 'Edit Document' : 'Create Document' }}
    </h1>

    <div class="box">
      <!-- === DOC HEAD === -->
      <div class="doc-head">
        <!-- Document type -->
        <div class="field">
          <label class="label">Document type</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="form.document_type" :disabled="isEditMode">
                <option value="invoice">Invoice (Client)</option>
                <option value="credit_note">Credit Note (Client)</option>
                <option value="quote">Quote</option>
                <option value="purchase_supplier">Supplier Invoice</option>
                <option value="supplier_credit_note">
                  Supplier Credit Note
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Client select + open details modal -->
        <div class="field">
          <label class="label">Client</label>
          <div class="control is-flex gap-8">
            <div class="select is-fullwidth" style="flex: 1">
              <select
                v-model.number="selectedClientId"
                @change="onSelectThirdParty"
              >
                <option :value="null">- Select client -</option>
                <option v-for="c in clients" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>
            <button
              class="button is-link"
              type="button"
              @click="openClientModal"
            >
              Client details
            </button>
          </div>
          <p class="help is-danger" v-if="clientError">
            Please select a client
          </p>
        </div>

        <!-- Client preview -->
        <div class="box" v-if="selectedClient">
          <p>
            <strong>{{ selectedClient.name }}</strong>
          </p>
          <p v-if="selectedClient.email">
            <strong>Email:</strong> {{ selectedClient.email }}
          </p>
          <p v-if="selectedClient.address1">{{ selectedClient.address1 }}</p>
          <p v-if="selectedClient.address2">{{ selectedClient.address2 }}</p>
          <p v-if="selectedClient.zipcode || selectedClient.place">
            {{ selectedClient.zipcode }} {{ selectedClient.place }}
          </p>
          <p v-if="selectedClient.country">{{ selectedClient.country }}</p>
          <p v-if="selectedClient.country_code">
            <strong>Country code:</strong> {{ selectedClient.country_code }}
          </p>
          <p v-if="selectedClient.region">
            <strong>Region:</strong> {{ selectedClient.region }}
          </p>
          <p v-if="selectedClient.peppol_endpoint_id">
            <strong>Peppol:</strong> {{ selectedClient.peppol_endpoint_id }}
            <span v-if="selectedClient.peppol_scheme_id">
              ({{ selectedClient.peppol_scheme_id }})</span
            >
          </p>
        </div>
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Issue date</label>
              <div class="control">
                <input class="input" type="date" v-model="form.issue_date" />
              </div>
            </div>
          </div>

          <div class="column is-6">
            <div class="field">
              <label class="label">Due date</label>
              <div class="control">
                <input class="input" type="date" v-model="form.due_date" />
              </div>
              <p class="help">
                If empty, due date will be calculated from due days.
              </p>
            </div>
          </div>
        </div>

        <!-- Extra fields -->
        <div class="field">
          <label class="label">Due days</label>
          <div class="control">
            <input
              class="input"
              type="number"
              min="0"
              v-model.number="form.due_days"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">Sender reference</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model.trim="form.sender_reference"
            />
          </div>
        </div>

        <!-- References (Peppol BT-10 / BT-13) -->
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Buyer reference (BT-10)</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model.trim="form.buyer_reference"
                  placeholder="e.g. IT-Dept / CostCenter-45"
                />
              </div>
              <p class="help">Provide buyer reference or a PO below.</p>
            </div>
          </div>
          <div class="column is-6">
            <div class="field">
              <label class="label">Order reference (PO)</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model.trim="form.order_reference"
                  placeholder="e.g. PO-2025-000123"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- === DOC DETAIL === -->
      <div class="doc-detail mt-5">
        <label class="label">Items</label>

        <table class="table is-fullwidth is-striped is-hoverable is-narrow">
          <thead>
            <tr>
              <th style="width: 20%">Title</th>
              <th style="width: 20%">Description</th>
              <th style="width: 8%">Qty</th>
              <th style="width: 10%">Unit Price</th>
              <th style="width: 9.5%">VAT %</th>
              <th style="width: 12%">Net</th>
              <th style="width: 12%">VAT</th>
              <th style="width: 20%">Gross</th>
              <th style="width: 11%">Actions</th>
            </tr>
          </thead>
          <tbody>
            <ItemForm
              v-for="(item, index) in form.document_items"
              :key="item._uuid || item.id || index"
              :initialItem="item"
              :itemIndex="index"
              :itemsAvailable="itemsAvailable"
              @updatePrice="onItemUpdate(index, $event)"
              @remove="removeItem(index)"
            />
          </tbody>
        </table>

        <div class="mt-3">
          <button
            class="button is-info is-light"
            type="button"
            @click="addItem"
          >
            + Add Item
          </button>
          <p class="has-text-grey mt-2">
            Total items: {{ form.document_items.length }}
          </p>
        </div>
      </div>
      <div class="field">
        <label class="label">Note</label>
        <div class="control">
          <input class="input" type="text" v-model.trim="form.note" />
        </div>
      </div>
      <!-- === DOC SUMMARY === -->
      <div class="doc-summary content mt-5" v-if="form.document_items.length">
        <p><strong>Net:</strong> {{ n2(totalNet) }} €</p>
        <p><strong>VAT:</strong> {{ n2(totalVat) }} €</p>
        <p>
          <strong>Gross:</strong>
          {{ form.document_type === 'credit_note' ? '-' : '' }}
          {{ n2(totalGross) }} €
        </p>
      </div>
      <div v-for="(amount, percent) in vatSummary" :key="percent">
        <p>VAT {{ percent }}%: {{ n2(amount) }} €</p>
      </div>

      <div class="buttons mt-4">
        <button
          class="button is-success"
          type="button"
          :disabled="isSaving"
          @click="submit(false)"
        >
          {{ isSaving ? 'Saving…' : isEditMode ? 'Update' : 'Save' }}
        </button>

        <button
          v-if="!isEditMode"
          class="button is-info"
          type="button"
          :disabled="isSaving"
          @click="submit(true)"
        >
          Save as Draft
        </button>

        <button class="button" type="button" @click="openCancelModal">
          Cancel
        </button>
      </div>
    </div>

    <!-- Cancel/draft modal -->
    <div class="modal" :class="{ 'is-active': showCancelModal }">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Leave page?</p>
          <button
            class="delete"
            aria-label="close"
            @click="closeCancelModal"
          ></button>
        </header>
        <section class="modal-card-body">
          You have unsaved changes. Do you want to save a draft before leaving?
        </section>
        <footer class="modal-card-foot">
          <button
            class="button is-info"
            type="button"
            :disabled="isSaving"
            v-if="!isEditMode"
            @click="submit(false, true)"
          >
            Save as Draft
          </button>
          <button class="button" type="button" @click="discardChanges">
            Discard
          </button>
        </footer>
      </div>
    </div>

    <!-- Client details modal -->
    <div class="modal" :class="{ 'is-active': isClientModalOpen }">
      <div class="modal-background" @click="isClientModalOpen = false"></div>
      <div class="modal-card" style="width: 840px; max-width: 95vw">
        <header class="modal-card-head">
          <p class="modal-card-title">Client details</p>
          <button
            class="delete"
            aria-label="close"
            @click="isClientModalOpen = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <div v-if="!selectedClientId" class="has-text-grey">
            Select a client first.
          </div>

          <div v-else class="content">
            <!-- Peppol readiness inline warning -->
            <article class="message is-warning" v-if="buyer && !peppolReady">
              <div class="message-body">
                Buyer electronic address (EndpointID) or scheme is missing.
              </div>
            </article>
            <div
              class="is-flex is-justify-content-space-between is-align-items-center mb-3"
            >
              <h4 class="title is-6 mb-0">
                {{ selectedClient?.name || 'Client' }}
              </h4>
              <label class="checkbox">
                <input type="checkbox" v-model="saveCustomerChanges" />
                Save changes to customer
              </label>
            </div>

            <div class="columns is-multiline">
              <!-- Legal -->
              <div class="column is-4">
                <div class="field">
                  <label class="label">Legal name</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.legal_name"
                    />
                  </div>
                </div>
              </div>
              <div class="column is-4">
                <div class="field">
                  <label class="label">VAT number</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.vat_number"
                    />
                  </div>
                </div>
              </div>
              <!-- Country / Region -->
              <div class="column is-4">
                <div class="field">
                  <label class="label">Country code</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select v-model="clientForm.country_code">
                        <option
                          v-for="c in COUNTRIES"
                          :key="c.value"
                          :value="c.value"
                        >
                          {{ c.value }} — {{ c.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column is-4">
                <div class="field">
                  <label class="label">Region / State</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.region"
                    />
                  </div>
                </div>
              </div>

              <!-- Contact -->
              <div class="column is-4">
                <div class="field">
                  <label class="label">Contact person</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.contact_person"
                    />
                  </div>
                </div>
              </div>
              <div class="column is-4">
                <div class="field">
                  <label class="label">Contact reference</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.contact_reference"
                    />
                  </div>
                </div>
              </div>

              <!-- Addresses -->
              <div class="column is-8">
                <div class="field">
                  <label class="label">Address line 1</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.address1"
                    />
                  </div>
                </div>
              </div>

              <div class="column is-8">
                <div class="field">
                  <label class="label">Address line 2</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.address2"
                    />
                  </div>
                </div>
              </div>

              <div class="column is-4">
                <div class="field">
                  <label class="label">Postal code</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.zipcode"
                    />
                  </div>
                </div>
              </div>

              <div class="column is-4">
                <div class="field">
                  <label class="label">City</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model="clientForm.place"
                    />
                  </div>
                </div>
              </div>

              <!-- Peppol -->
              <div class="column is-4">
                <div class="field">
                  <label class="label">Peppol endpoint</label>
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="e.g. 0123456789"
                      v-model="clientForm.peppol_endpoint"
                    />
                  </div>
                  <!-- Keep help minimal: backend requires endpoint + scheme -->
                  <p class="help">
                    Provide the buyer’s Peppol electronic address.
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="field">
                  <label class="label">Peppol scheme</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select v-model="clientForm.peppol_scheme">
                        <option
                          v-for="s in PEPPOL_SCHEMES"
                          :key="s.value"
                          :value="s.value"
                        >
                          {{ s.value }} — {{ s.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <p class="help">Default: 9930 (VAT)</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button
            class="button"
            type="button"
            @click="isClientModalOpen = false"
          >
            Close
          </button>
          <button
            class="button is-primary"
            type="button"
            :disabled="
              !selectedClientId ||
              (saveCustomerChanges && buyer && !peppolReady)
            "
            @click="saveClientDetails"
          >
            Save details
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
/* Comments in English */
import api from '@/utils/axios';
import { toast } from 'bulma-toast';
import ItemForm from '@/components/ItemForm.vue';

export default {
  name: 'AddDocument',
  components: { ItemForm },
  props: {
    editMode: { type: Boolean, default: false },
  },
  data() {
    return {
      clients: [],
      selectedClientId: null,
      clientError: false,

      itemsAvailable: [],

      form: {
        document_type: 'invoice',
        issue_date: null,
        due_date: null,
        due_days: 14,
        sender_reference: '',
        buyer_reference: '',
        order_reference: '',
        document_items: [],
        number: null,
        is_credit_for: null,
      },

      // Modal state & client form
      isClientModalOpen: false,
      saveCustomerChanges: false,
      clientForm: {
        country_code: 'BE',
        region: '',
        address1: '',
        address2: '',
        zipcode: '',
        place: '',
        contact_person: '',
        contact_reference: '',
        peppol_endpoint: '',
        peppol_scheme: '9930',
      },

      isSaving: false,
      hasChanges: false,
      showCancelModal: false,
      pendingNext: null,

      COUNTRIES: [
        { value: 'BE', label: 'Belgium' },
        { value: 'FR', label: 'France' },
        { value: 'NL', label: 'Netherlands' },
        { value: 'DE', label: 'Germany' },
        { value: 'LU', label: 'Luxembourg' },
      ],
      PEPPOL_SCHEMES: [
        { value: '9930', label: 'VAT number' },
        { value: '0208', label: 'CBE (Belgium)' },
        { value: '0088', label: 'GLN' },
      ],
    };
  },
  computed: {
    isEditMode() {
      return this.editMode || Boolean(this.$route.params?.id);
    },
    peppolReady() {
      const tp = this.buyer || this.selectedClient; // selon ce que tu charges
      return !!(tp && tp.peppol_endpoint && tp.peppol_scheme);
    },

    selectedClient() {
      return this.clients.find((c) => c.id === this.selectedClientId) || null;
    },
    totalNet() {
      return this.form.document_items.reduce((sum, it) => {
        const q = Number(it.quantity || 0);
        const p = Number(it.unit_price || 0);
        return sum + q * p;
      }, 0);
    },
    totalVat() {
      return this.form.document_items.reduce((sum, it) => {
        const q = Number(it.quantity || 0);
        const p = Number(it.unit_price || 0);
        const percent = it.vat_code_detail?.percent
          ? Number(it.vat_code_detail.percent)
          : 0;
        return sum + q * p * (percent / 100);
      }, 0);
    },
    vatSummary() {
      const summary = {};
      this.form.document_items.forEach((it) => {
        const q = Number(it.quantity || 0);
        const p = Number(it.unit_price || 0);
        const percent = it.vat_code_detail?.percent
          ? Number(it.vat_code_detail.percent)
          : 0;
        const vat = q * p * (percent / 100);
        summary[percent] = (summary[percent] || 0) + vat;
      });
      return summary;
    },
    totalGross() {
      return this.totalNet + this.totalVat;
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.hasChanges = true;
      },
    },
    selectedClientId() {
      this.hasChanges = true;
      this.clientError = false;
      this.onSelectThirdParty();
    },
  },
  async mounted() {
    await Promise.all([this.fetchClients(), this.fetchItems()]);

    if (this.isEditMode) {
      await this.loadDocumentForEdit();
    } else if (this.$route.query.preset) {
      try {
        const preset = JSON.parse(this.$route.query.preset);

        this.selectedClientId = preset.third_party;
        this.form.document_type = preset.document_type || 'invoice';
        this.form.due_days = preset.due_days ?? 14;
        this.form.sender_reference = preset.sender_reference || '';
        this.form.buyer_reference = preset.buyer_reference || '';
        this.form.order_reference = preset.order_reference || '';
        this.form.number = null;
        this.form.is_credit_for = preset.is_credit_for || null;

        this.form.document_items = (preset.document_items || []).map((it) => ({
          _uuid: Date.now() + Math.random(),
          id: null,
          item_id: it.item_id || null,
          title: it.title || '',
          description: it.description || '',
          quantity: it.quantity ?? '0',
          unit_price: it.unit_price ?? '0',
          vat_rate: Number(it.vat_rate || 0),
          vat_code: it.vat_code || null,
          discount: Number(it.discount || 0),
        }));

        this.hasChanges = true;
      } catch (e) {
        console.error('Invalid preset payload', e);
      }
    } else if (!this.form.document_items.length) {
      this.addItem();
    }
  },

  methods: {
    async fetchClients() {
      try {
        const resp = await api.get('/third-parties/');
        const payload = resp.data;
        this.clients = Array.isArray(payload)
          ? payload
          : payload?.results || [];
      } catch (e) {
        console.error(e);
        toast({ message: 'Failed to load clients', type: 'is-danger' });
      }
    },
    async fetchItems() {
      try {
        const resp = await api.get('/items/');
        const payload = resp.data;
        this.itemsAvailable = Array.isArray(payload)
          ? payload
          : payload?.results || [];
      } catch (e) {
        console.error(e);
        toast({ message: 'Failed to load items', type: 'is-danger' });
      }
    },
    toDecimal(v) {
      return parseFloat(String(v).replace(',', '.')) || 0;
    },
    async loadDocumentForEdit() {
      try {
        const id = this.$route.params.id;
        const resp = await api.get(`/documents/${id}/`);
        const doc = resp.data;

        this.selectedClientId = doc.third_party ?? null;

        this.form.document_type = doc.document_type || 'invoice';
        this.form.issue_date = doc.issue_date || null;
        this.form.due_date = doc.due_date || null;
        this.form.due_days = doc.due_days ?? 14;
        this.form.sender_reference = doc.sender_reference || '';
        this.form.buyer_reference = doc.buyer_reference || '';
        this.form.order_reference = doc.order_reference || '';
        this.form.number = doc.number || null;
        this.form.note = doc.note || null;

        const mapped = (doc.document_items || []).map((it) => ({
          _uuid: Date.now() + Math.random(),
          id: it.id || null,
          item_id: it.item?.id || it.item_id || null,
          title: it.title || '',
          description: it.description || '',
          quantity: Number(it.quantity || 1),
          unit_price: Number(it.unit_price || 0),
          vat_rate: Number(it.vat_rate || 0),
          vat_code: it.vat_code || null,
          discount: Number(it.discount || 0),
        }));
        this.form.document_items = mapped;

        this.hasChanges = false;
      } catch (e) {
        console.error('Failed to load document for editing', e);
        toast({ message: 'Failed to load document.', type: 'is-danger' });
      }
    },

    // ----- Client modal helpers -----
    onSelectThirdParty() {
      const tp = this.selectedClient;
      if (!tp) return;
      this.clientForm = {
        country_code: tp.country_code || 'BE',
        region: tp.region || '',
        address1: tp.address1 || '',
        address2: tp.address2 || '',
        zipcode: tp.zipcode || '',
        place: tp.place || '',
        contact_person: tp.contact_person || '',
        contact_reference: tp.contact_reference || '',
        peppol_endpoint: tp.peppol_endpoint || '',
        peppol_scheme: tp.peppol_scheme || '9930',
      };
    },
    openClientModal() {
      if (!this.selectedClientId) {
        this.clientError = true;
        toast({ message: 'Please select a client first', type: 'is-warning' });
        return;
      }
      this.onSelectThirdParty();
      this.isClientModalOpen = true;
    },
    async saveClientDetails() {
      if (!this.selectedClientId) return;
      if (this.saveCustomerChanges) {
        try {
          await api.patch(`/third-parties/${this.selectedClientId}/`, {
            region: this.clientForm.region,
            address1: this.clientForm.address1,
            address2: this.clientForm.address2,
            zipcode: this.clientForm.zipcode,
            place: this.clientForm.place,
            contact_person: this.clientForm.contact_person || null,
            contact_reference: this.clientForm.contact_reference || null,
            country_code: this.clientForm.country_code || 'BE',
            legal_name: this.clientForm.legal_name || null,
            vat_number: this.clientForm.vat_number || null,
            peppol_endpoint: this.clientForm.peppol_endpoint_id || null,
            peppol_scheme: this.clientForm.peppol_scheme_id || '9930',
          });
          const idx = this.clients.findIndex(
            (c) => c.id === this.selectedClientId
          );
          if (idx !== -1)
            this.clients[idx] = { ...this.clients[idx], ...this.clientForm };
          toast({ message: 'Customer updated', type: 'is-success' });
        } catch (e) {
          console.error(e);
          toast({ message: 'Failed to update customer', type: 'is-danger' });
          return;
        }
      }
      this.isClientModalOpen = false;
    },

    // ----- Items -----
    addItem() {
      this.form.document_items.push({
        _uuid: Date.now() + Math.random(),
        id: null,
        item_id: null,
        title: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        vat_rate: 0,
        vat_code: null,
        discount: 0,
      });
    },
    removeItem(index) {
      this.form.document_items.splice(index, 1);
      this.hasChanges = true;
    },
    onItemUpdate(index, partial) {
      this.form.document_items[index] = {
        ...this.form.document_items[index],
        ...partial,
      };
      this.hasChanges = true;
    },

    // ----- Utils -----
    n2(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    },

    // ----- Submit -----
    async submit(isDraft = true, fromModal = false) {
      if (!this.selectedClientId) {
        this.clientError = true;
        toast({ message: 'Please select a client', type: 'is-danger' });
        return;
      }

      if (!this.form.document_items.length) {
        toast({ message: 'Please add at least one item', type: 'is-warning' });
        return;
      }

      const hasNegativeQuantity = this.form.document_items.some(
        (it) => Number(it.quantity) < 0
      );

      if (hasNegativeQuantity) {
        toast({
          message: 'Quantity must be zero or positive.',
          type: 'is-danger',
          position: 'bottom-right',
        });
        return;
      }

      try {
        this.isSaving = true;

        const lines = this.form.document_items.map((it) => {
          const line = {
            title: it.title,
            description: it.description,
            quantity: this.toDecimal(it.quantity),
            unit_price: this.toDecimal(it.unit_price),
            discount: this.toDecimal(it.discount),
          };

          if (it.item_id) line.item_id = it.item_id;
          if (it.id) line.id = it.id;
          if (it.vat_code) line.vat_code = it.vat_code;

          return line;
        });

        const basePayload = {
          document_type: this.form.document_type,
          third_party: this.selectedClientId,
          issue_date: this.form.issue_date || null,
          due_date: this.form.due_date || null,
          due_days: this.form.due_days,
          sender_reference: this.form.sender_reference,
          buyer_reference: this.form.buyer_reference || null,
          order_reference: this.form.order_reference || null,
          document_items: lines,
          is_draft: isDraft,
        };

        if (this.form.is_credit_for) {
          basePayload.is_credit_for = this.form.is_credit_for;
        }

        let result;

        if (this.isEditMode) {
          const resp = await api.patch(
            `/documents/${this.$route.params.id}/`,
            basePayload
          );

          result = resp.data;

          toast({
            message: 'Document updated',
            type: 'is-success',
            duration: 1600,
            position: 'bottom-right',
          });
        } else {
          const resp = await api.post('/documents/', basePayload);

          result = resp.data;

          toast({
            message: isDraft ? 'Draft saved' : 'Document saved',
            type: 'is-success',
            duration: 1800,
            position: 'bottom-right',
          });
        }

        this.hasChanges = false;

        if (fromModal && typeof this.pendingNext === 'function') {
          const next = this.pendingNext;
          this.pendingNext = null;
          next();
          return;
        }

        this.$router.push({
          name: 'DetailDocument',
          params: { id: result.id },
        });
      } catch (e) {
        console.error('Save failed:', e);

        const backendMsg =
          e?.response?.data?.detail ||
          (typeof e?.response?.data === 'string' ? e.response.data : null) ||
          e?.message ||
          'Save failed';

        toast({
          message: backendMsg,
          type: 'is-danger',
        });
      } finally {
        this.isSaving = false;
        this.showCancelModal = false;
      }
    },

    // ----- Cancel / guard -----
    openCancelModal() {
      if (!this.hasChanges) {
        this.$router.back();
        return;
      }
      this.showCancelModal = true;
    },
    closeCancelModal() {
      this.showCancelModal = false;
      if (typeof this.pendingNext === 'function') {
        const next = this.pendingNext;
        this.pendingNext = null;
        next(false);
      }
    },
    discardChanges() {
      this.hasChanges = false;
      this.showCancelModal = false;
      if (typeof this.pendingNext === 'function') {
        const next = this.pendingNext;
        this.pendingNext = null;
        next();
      } else {
        this.$router.push('/dashboard/documents');
      }
    },
  },

  // Route leave guard
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges || this.isSaving) return next();
    this.pendingNext = next;
    this.showCancelModal = true;
  },
};
</script>

<style scoped>
.table td,
.table th {
  vertical-align: middle;
}
.select,
.select select {
  width: 100%;
}
.gap-8 {
  gap: 0.75rem;
}
</style>
